import { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { Controller } from "react-hook-form";
import SearchInput from "@/components/SearchInput";
import { upperCase, reject, isEmpty } from "lodash";
import { useSearch } from "@/hooks/useSearch";
import { FormProvider, useForm } from "react-hook-form";
import DateSelect from "@/components/DateSelect";
import MultiSelect from "@/components/MultiSelect";
import { DateValueType } from "react-tailwindcss-datepicker";
import FacilityAutocomplete from "../../../_components/FacilityAutocomplete";
import SectionAutocomplete from "../../../_components/SectionAutocomplete";
import { get } from "lodash";
import { GET_CLASSIFICATIONS } from "../../../queries";
import { ReportType, Variables } from "./PeopleTable";
import { useParams } from "@/hooks/useSearchParams";
import { TableControls } from "@/components/Table";
import type { ReactNode } from "react";

export type FormValues = {
  memberClassifications: string[];
  program: string[];
  sectionIds: string[];
  facilityIds: string[];
  suspensionDateRange: DateValueType | null;
  terminationDateRange: DateValueType | null;
};

export const PeopleTableFilters = ({
  reportType,
  defaultVariables,
  setVariables,
  variables,
  resetPagination,
  pagination,
  onSortClick
}: {
  reportType: ReportType;
  defaultVariables: Variables;
  setVariables: React.Dispatch<React.SetStateAction<Variables>>;
  variables: Variables;
  resetPagination: () => void;
  pagination: ReactNode;
  onSortClick?: () => void;
}) => {
  const isTerminations = reportType === "terminations";
  let { params, addSearchParam, deleteSearchParams } = useParams();
  const { searchValue, control: controlSearch, setValue: setSearchValue } = useSearch("name", params.name || "");

  const [selectedValues, setSelectedValues] = useState<string[]>(params.programTypes || []);
  const [selectedClassificationValues, setSelectedClassificationValues] = useState<string[]>(
    params.memberClassifications || []
  );

  const { data: classificationData } = useQuery(GET_CLASSIFICATIONS);

  const form = useForm<FormValues>();
  const { control, reset, watch } = form;

  const sectionIds = watch("sectionIds");
  const facilityIds = watch("facilityIds");

  useEffect(() => {
    setVariables((prevValue) => ({
      ...prevValue,
      sectionIds: sectionIds,
      facilityIds: facilityIds
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIds, facilityIds]);

  useEffect(() => {
    setVariables((prevValue) => {
      const { search: _search, ...restVariables } = prevValue;
      return {
        ...restVariables,
        ...(searchValue && { search: searchValue })
      };
    });
  }, [searchValue, setVariables]);

  const filters = {
    memberClassifications: selectedClassificationValues.length > 0,
    program: selectedValues.length > 0,
    facility: (variables.facilityIds?.length ?? 0) > 0,
    reestablished: isTerminations && variables.reestablished !== false,
    terminationDateRange: !!variables?.terminatedDateRange?.from || !!variables?.terminatedDateRange?.to,
    section: (variables.sectionIds?.length ?? 0) > 0
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const classificationsNodes = get(classificationData, "classifications.nodes");
  const classificationDataNodes =
    classificationsNodes?.map((node: any) => ({
      value: node.name,
      label: node.name
    })) || [];

  const onClearAll = () => {
    setVariables({ ...defaultVariables });
    setSelectedValues([]);
    setSelectedClassificationValues([]);
    reset({
      suspensionDateRange: { startDate: null, endDate: null },
      terminationDateRange: { startDate: null, endDate: null }
    });
    deleteSearchParams([
      "memberClassifications[]",
      "programTypes[]",
      "facilityIds[]",
      "sectionIds[]",
      "facility",
      "section",
      "reestablished",
      "name"
    ]);
    setSearchValue("name", "");
  };

  const setReestablished = (next: boolean | null) => {
    setVariables((prevValue) => ({
      ...prevValue,
      reestablished: next
    }));
    if (next === null) {
      deleteSearchParams("reestablished");
    } else {
      addSearchParam("reestablished", String(next));
    }
    resetPagination();
  };

  const reestablishedFilterChips = isTerminations
    ? [
        {
          label: "Currently Terminated",
          selected: variables.reestablished === false,
          onClick: () => setReestablished(false)
        },
        {
          label: "Re-established",
          selected: variables.reestablished === true,
          onClick: () => setReestablished(true)
        },
        {
          label: "All",
          selected: variables.reestablished == null,
          onClick: () => setReestablished(null)
        }
      ]
    : undefined;

  return (
    <FormProvider {...form}>
      <TableControls
        pagination={pagination}
        onSortClick={onSortClick}
        useExpandedFilters
        expandedFiltersState={filters}
        activeFilters={activeFilterCount}
        onClearAllFilters={() => {
          onClearAll();
          resetPagination();
        }}
        filterChips={reestablishedFilterChips}
        filterChipsAriaLabel={isTerminations ? "Re-establishment" : undefined}
      >
        <Controller
          name="name"
          control={controlSearch}
          render={({ field }) => (
            <SearchInput
              placeholder="Search People"
              query={field.value}
              onChange={(e) => {
                field.onChange(e);
                resetPagination();
                addSearchParam("name", e);
              }}
              onClear={() => {
                deleteSearchParams("name");
                setSearchValue("name", "");
              }}
            />
          )}
        />
        <div data-name="memberClassifications">
          <Controller
            name="memberClassifications"
            control={control}
            render={() => (
              <MultiSelect
                label="Classification"
                selectedValues={selectedClassificationValues}
                options={classificationDataNodes}
                onSelect={(value, checked) => {
                  const status = checked
                    ? [...selectedClassificationValues, value]
                    : selectedClassificationValues.filter((v) => v !== value);
                  setSelectedClassificationValues(reject(status, isEmpty));
                  setVariables((prevValue) => ({
                    ...prevValue,
                    memberClassifications: reject(status, isEmpty)
                  }));
                  addSearchParam("memberClassifications[]", reject(status, isEmpty).join(","));
                }}
                onClear={() => {
                  setSelectedClassificationValues([]);
                  setVariables((prevValue) => ({
                    ...prevValue,
                    memberClassifications: []
                  }));
                  deleteSearchParams("memberClassifications[]");
                }}
                className="w-full"
              />
            )}
          />
        </div>
        <div data-name="facility">
          <FacilityAutocomplete
            sectionIds={variables?.sectionIds}
            addSearchParam={addSearchParam}
            deleteSearchParams={deleteSearchParams}
            params={params}
          />
        </div>
        <div data-name="program">
          <Controller
            name="program"
            control={control}
            render={() => (
              <MultiSelect
                label="Program"
                selectedValues={selectedValues}
                options={[
                  { value: "MEMBER", label: "Member (MB)" },
                  { value: "ASSOCIATE", label: "Associate (AP)" },
                  { value: "AFFILIATE", label: "Affiliate (AF)" }
                ]}
                onSelect={(value, checked) => {
                  const status = checked ? [...selectedValues, value] : selectedValues.filter((v) => v !== value);
                  const statusValues = status.map((status) => upperCase(status));
                  setSelectedValues(reject(status, isEmpty));
                  setVariables((prevValue) => ({
                    ...prevValue,
                    programTypes: reject(statusValues, isEmpty)
                  }));
                  addSearchParam("programTypes[]", reject(status, isEmpty).join(","));
                }}
                onClear={() => {
                  setSelectedValues([]);
                  setVariables((prevValue) => ({
                    ...prevValue,
                    programTypes: []
                  }));
                  deleteSearchParams("programTypes[]");
                }}
                className="w-full"
              />
            )}
          />
        </div>
        {isTerminations && (
          <div data-name="terminationDateRange" data-filter-size="custom">
            <Controller
              name="terminationDateRange"
              control={control}
              render={({ field }) => (
                <DateSelect
                  placeholder="Select Dates"
                  label="Termination Date"
                  useModal={true}
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e);
                    if (typeof e === "object" && e !== null) {
                      setVariables((prevValue) => ({
                        ...prevValue,
                        terminatedDateRange: {
                          from: e.startDate,
                          to: e.endDate
                        }
                      }));
                      resetPagination();
                    }
                  }}
                />
              )}
            />
          </div>
        )}
        <div data-name="section">
          <SectionAutocomplete
            addSearchParam={addSearchParam}
            deleteSearchParams={deleteSearchParams}
            params={params}
            name="section"
          />
        </div>
      </TableControls>
    </FormProvider>
  );
};

export default PeopleTableFilters;
